import { useState } from "react";
import { apiUrl, readApiJson } from "../utils/api";
import "../style/Quiz.css";

const AUTH_API_URL = apiUrl("auth.php");

export default function AuthPage({ text, onAuthenticated }) {
    const [mode, setMode] = useState("login");
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [password, setPassword] = useState("");
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [failedLoginAttempts, setFailedLoginAttempts] = useState(0);
    const [resetRequestStatus, setResetRequestStatus] = useState("");
    const [isRequestingReset, setIsRequestingReset] = useState(false);
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isRegistering = mode === "register";

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError("");
        setResetRequestStatus("");
        setIsSubmitting(true);

        try {
            const response = await fetch(AUTH_API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    action: mode,
                    name,
                    phone,
                    password
                })
            });
            const result = await readApiJson(response, "Authentication API");

            if (!response.ok) {
                if (mode === "register" && result.error === "phone_exists") {
                    setMode("login");
                    setPassword("");
                    setIsPasswordVisible(false);
                }
                if (mode === "login" && result.error === "invalid_credentials") {
                    setFailedLoginAttempts((attempts) => attempts + 1);
                }
                setError(text.authErrors[result.error] || text.authGenericError);
                return;
            }

            setFailedLoginAttempts(0);
            onAuthenticated(result.user);
        } catch (requestError) {
            console.error("Authentication request failed:", requestError);
            setError(`${text.authConnectionError} ${requestError.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePasswordResetRequest = async () => {
        setError("");
        setResetRequestStatus("");
        setIsRequestingReset(true);

        try {
            const response = await fetch(AUTH_API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    action: "request_password_reset",
                    phone
                })
            });
            const result = await readApiJson(response, "Authentication API");
            if (!response.ok) {
                setError(text.authErrors[result.error] || text.authGenericError);
                return;
            }
            setResetRequestStatus(text.passwordResetRequestSubmitted);
        } catch (requestError) {
            console.error("Password reset request failed:", requestError);
            setError(`${text.authConnectionError} ${requestError.message}`);
        } finally {
            setIsRequestingReset(false);
        }
    };

    const changeMode = () => {
        setMode((currentMode) => currentMode === "login" ? "register" : "login");
        setIsPasswordVisible(false);
        setFailedLoginAttempts(0);
        setResetRequestStatus("");
        setError("");
    };

    return (
        <main className="auth-page">
            <section className="auth-card" aria-labelledby="auth-title">
                <span className="quiz-label">{text.authWelcome}</span>
                <h1 id="auth-title">
                    {isRegistering ? text.signUpTitle : text.loginTitle}
                </h1>
                <p className="auth-description">
                    {isRegistering ? text.signUpDescription : text.loginDescription}
                </p>

                <form className="auth-form" onSubmit={handleSubmit}>
                    {isRegistering && (
                        <div className="auth-field">
                            <label htmlFor="auth-name">{text.nameLabel}</label>
                            <input
                                id="auth-name"
                                type="text"
                                autoComplete="name"
                                maxLength={100}
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                                required
                            />
                        </div>
                    )}
                    <div className="auth-field">
                        <label htmlFor="auth-phone">{text.phoneLabel}</label>
                        <input
                            id="auth-phone"
                            type="tel"
                            autoComplete="tel"
                            inputMode="tel"
                            placeholder={text.phonePlaceholder}
                            maxLength={24}
                            value={phone}
                            onChange={(event) => {
                                setPhone(event.target.value);
                                setFailedLoginAttempts(0);
                                setResetRequestStatus("");
                            }}
                            required
                        />
                        <small>{text.phoneHint}</small>
                    </div>
                    <div className="auth-field">
                        <label htmlFor="auth-password">{text.passwordLabel}</label>
                        <div className="auth-password-input-wrap">
                            <input
                                id="auth-password"
                                type={isPasswordVisible ? "text" : "password"}
                                autoComplete={isRegistering ? "new-password" : "current-password"}
                                minLength={8}
                                maxLength={4096}
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                required
                            />
                            <button
                                className="auth-password-visibility"
                                type="button"
                                aria-label={isPasswordVisible ? text.hidePassword : text.showPassword}
                                aria-pressed={isPasswordVisible}
                                onClick={() => setIsPasswordVisible((visible) => !visible)}
                            >
                                {isPasswordVisible ? text.hidePassword : text.showPassword}
                            </button>
                        </div>
                        {isRegistering && (
                            <small>{text.passwordHint}</small>
                        )}
                    </div>

                    {error && (
                        <p className="auth-error" role="alert">
                            {error}
                        </p>
                    )}
                    {resetRequestStatus && (
                        <p className="auth-reset-request-notice" role="status">
                            {resetRequestStatus}
                        </p>
                    )}
                    {!isRegistering && !resetRequestStatus && failedLoginAttempts >= 3 && (
                        <p className="auth-reset-request-notice" role="status">
                            {text.loginResetRequestNotice}
                        </p>
                    )}

                    <button className="quiz-btn auth-submit" type="submit" disabled={isSubmitting}>
                        {isSubmitting
                            ? text.authSubmitting
                            : isRegistering ? text.signUpButton : text.loginButton}
                    </button>
                    {!isRegistering && (
                        <button
                            className="auth-password-reset-request"
                            type="button"
                            onClick={handlePasswordResetRequest}
                            disabled={isRequestingReset || isSubmitting}
                        >
                            {isRequestingReset
                                ? text.authSubmitting
                                : text.requestPasswordReset}
                        </button>
                    )}
                </form>

                <p className="auth-switch">
                    {isRegistering ? text.haveAccount : text.needAccount}
                    <button type="button" onClick={changeMode}>
                        {isRegistering ? text.loginButton : text.signUpButton}
                    </button>
                </p>
            </section>
        </main>
    );
}
